// ============================================================
// Viashift — Auth helpers (Phase 1, Epics 1B.2 + 1B.6)
// ============================================================
// Utilities shared across auth-aware components:
//
//   - safeNextPath / buildLoginRedirect: validate and build the
//     `?next=` parameter that preserves a user's intended
//     destination across the login round-trip. Unvalidated `next`
//     params are a classic open-redirect vector — a link like
//     /login?next=//evil.com/phish would land an authenticated
//     user on an attacker's page with full browser trust. The
//     validator rejects anything not a same-origin app path.
//
//   - LOGOUT_INTENT_KEY / isLogoutInProgress / clearLogoutIntent:
//     coordination flag between the explicit logout flow and the
//     auth guard. The logout() helper sets the flag before calling
//     signOut(). The guard reads and clears the flag on SIGNED_OUT
//     so it can distinguish "user signed out intentionally" (no
//     banner) from "session died unexpectedly" (banner on /login).
//
//   - logout: the canonical sign-out flow. Used by Header (1B.6)
//     and the error-screen sign-out button in App.tsx.
// ============================================================

import { supabase } from './supabase';

/**
 * Default landing page when no `next` is specified or the provided
 * `next` fails validation. Matches the build plan's Phase 1 exit
 * criteria ("land on an empty Scan page").
 */
export const DEFAULT_POST_LOGIN_PATH = '/scan';

// Public routes that must never be used as a `next` destination.
// Navigating to /login or /auth/callback as a "landing page" would
// loop (already-authed users get bounced) or make no sense.
const RESERVED_PUBLIC_PATHS = new Set<string>(['/login', '/auth/callback']);

/**
 * Validate a `next` parameter so it can only redirect to an internal
 * app path. Rejects:
 *
 *   - missing / empty
 *   - anything not starting with `/` (relative paths, external URLs)
 *   - `//...` (protocol-relative — browsers treat these as
 *     same-scheme external redirects)
 *   - the public auth pages themselves (would loop)
 *
 * Returns the validated path on accept, or DEFAULT_POST_LOGIN_PATH
 * on reject. The caller does not need to re-check.
 */
export function safeNextPath(raw: string | null | undefined): string {
  if (!raw) return DEFAULT_POST_LOGIN_PATH;
  if (!raw.startsWith('/')) return DEFAULT_POST_LOGIN_PATH;
  if (raw.startsWith('//')) return DEFAULT_POST_LOGIN_PATH;

  // Strip query and hash to isolate the pathname. split() always
  // returns a non-empty array, but noUncheckedIndexedAccess forces
  // the explicit fallback.
  const withoutHash = raw.split('#')[0] ?? raw;
  const pathname = withoutHash.split('?')[0] ?? withoutHash;
  if (RESERVED_PUBLIC_PATHS.has(pathname)) return DEFAULT_POST_LOGIN_PATH;

  return raw;
}

/**
 * Build a `/login?next=...` path from an intended destination. Used
 * by the auth guard when bouncing an unauthenticated user. Omits the
 * `next` param when the destination is the default landing page
 * (keeps URLs clean in the common case).
 */
export function buildLoginRedirect(targetPath: string): string {
  if (!targetPath) return '/login';
  const safe = safeNextPath(targetPath);
  if (safe === DEFAULT_POST_LOGIN_PATH) return '/login';
  return `/login?next=${encodeURIComponent(safe)}`;
}

// ---- Logout coordination -------------------------------------------
//
// sessionStorage (not localStorage) so the flag is automatically
// cleared when the tab closes. Scoped to the single logout event,
// never long-lived.

export const LOGOUT_INTENT_KEY = 'viashift-logout-intent';

/**
 * True if an explicit logout is in progress. The auth guard checks
 * this when a session ends so it can skip the "session expired"
 * banner on intentional sign-outs.
 *
 * 1B.6 will set the flag via sessionStorage.setItem before calling
 * supabase.auth.signOut(). The guard clears it after reading.
 */
export function isLogoutInProgress(): boolean {
  try {
    return sessionStorage.getItem(LOGOUT_INTENT_KEY) === '1';
  } catch {
    // sessionStorage can throw in locked-down browser contexts
    // (private mode quotas, disabled storage). Fail safe: treat
    // as "not intentional" so the user sees the expiry banner
    // rather than a silent redirect.
    return false;
  }
}

export function clearLogoutIntent(): void {
  try {
    sessionStorage.removeItem(LOGOUT_INTENT_KEY);
  } catch {
    /* ignore — see isLogoutInProgress */
  }
}

// ---- Sign-out flow -------------------------------------------------
//
// Single source of truth for "the user clicked sign out." Sets the
// intent flag first so the auth guard's onAuthStateChange listener
// can distinguish this from session expiry, then calls supabase
// signOut. The auth guard handles the redirect via SIGNED_OUT.
//
// Callers should fire-and-forget (`void logout()`); errors are
// logged but not thrown — surfacing a "couldn't sign out" toast
// from the header would be alarming and the user can simply tap
// again. If signOut fails, the local session may or may not be
// cleared depending on supabase-js's local fallback behavior;
// either way the user can re-attempt.

export async function logout(): Promise<void> {
  try {
    sessionStorage.setItem(LOGOUT_INTENT_KEY, '1');
  } catch {
    // sessionStorage failures fall through to the default
    // "Your session expired" banner on the redirect — see
    // isLogoutInProgress for context.
  }

  const { error } = await supabase.auth.signOut();
  if (error) {
    console.error('signOut failed:', error);
    // Clear the intent flag — if the session is still alive,
    // a future expiry should still surface the expired banner.
    clearLogoutIntent();
  }
}
